/**
 * iter236 · MOVEMENT CALENDAR — one component, both directions.
 *
 *   Purchase inward  →  production  →  sales bill (GST / Non-GST) = dispatch out
 *
 * Outward: a bill IS the movement. A dispatch card attached to it only carries
 * transport details, so the kg is never counted twice.
 * Inward: only a goods-receipt note counts as arrived. Vendor bills are the
 * money layer, purchase orders are faint expected markers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, RotateCw, ArrowDownLeft, ArrowUpRight,
  AlertTriangle, ShieldCheck, Wrench,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MovementDayPanel, { STAGE_TONE, money } from './MovementDayPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LAYERS = [
  { k: 'movement', l: 'Arrived' },
  { k: 'money', l: 'Bills (money)' },
  { k: 'expected', l: 'Expected (PO)' },
];

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export default function MovementCalendar({ direction = 'outward' }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const outward = direction === 'outward';
  const accent = outward ? 'emerald' : 'amber';

  const [searchParams] = useSearchParams();
  const [cursor, setCursor] = useState(() => {
    const q = searchParams.get('month');
    if (q && /^\d{4}-\d{2}$/.test(q)) {
      const [y, mo] = q.split('-').map(Number);
      return new Date(y, mo - 1, 1);
    }
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [entity, setEntity] = useState('all');
  const [basis, setBasis] = useState('movement');
  const [layers, setLayers] = useState(['movement', 'money', 'expected']);
  const [data, setData] = useState(null);
  const [recon, setRecon] = useState(null);
  const [showRecon, setShowRecon] = useState(false);
  const [openDate, setOpenDate] = useState('');
  const [transporters, setTransporters] = useState([]);
  const [loading, setLoading] = useState(true);

  const month = monthKey(cursor);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month, entity };
      if (outward) params.date_basis = basis;
      else params.layers = layers.length === 3 ? 'all' : (layers.join(',') || 'movement');
      const [r, rc] = await Promise.all([
        axios.get(`${API}/movements/${direction}`, { params, withCredentials: true }),
        axios.get(`${API}/movements/reconciliation`, { params: { month }, withCredentials: true })
          .catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setRecon(rc.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the movement calendar');
    } finally { setLoading(false); }
  }, [direction, month, entity, basis, layers, outward]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!outward) return;
    axios.get(`${API}/dispatch/transporters`, { withCredentials: true })
      .then(r => setTransporters(r.data || [])).catch(() => {});
  }, [outward]);

  const dayMap = useMemo(() => {
    const m = {};
    (data?.days || []).forEach(d => { m[d.date] = d; });
    return m;
  }, [data]);

  const cells = useMemo(() => {
    const y = cursor.getFullYear(), mo = cursor.getMonth();
    const lead = new Date(y, mo, 1).getDay();
    const total = new Date(y, mo + 1, 0).getDate();
    const out = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= total; d++) {
      out.push(`${y}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    return out;
  }, [cursor]);

  const m = data?.metrics;
  const cp = data?.counterpart;
  const recDir = recon?.[direction] || {};
  const recCount = outward
    ? (recDir.billed_not_loaded?.length || 0) + (recDir.gone_not_billed?.length || 0) + (recDir.double_stock_deduction?.length || 0)
    : (recDir.overdue_pos?.length || 0) + (recDir.grn_without_bill?.length || 0);

  const fixStock = async (id) => {
    try {
      const r = await axios.post(`${API}/movements/fix-double-stock/${id}`, {}, { withCredentials: true });
      toast.success(`Restored ${r.data.restored?.length || 0} product(s) to stock`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not fix'); }
  };

  const cards = outward ? [
    { l: 'Goods out', v: `${(m?.kg || 0).toLocaleString()} kg`, tone: 'text-emerald-400' },
    { l: 'Billed value', v: money(m?.amount), tone: 'text-zinc-100' },
    { l: 'Ready to load', v: m?.stages?.billed_not_loaded || 0, tone: (m?.stages?.billed_not_loaded ? 'text-amber-400' : 'text-zinc-400') },
    { l: 'In transit', v: m?.stages?.in_transit || 0, tone: 'text-sky-400' },
    { l: 'Delivered', v: m?.stages?.delivered || 0, tone: 'text-emerald-400' },
    { l: 'Gone, not billed', v: m?.stages?.unbilled || 0, tone: (m?.stages?.unbilled ? 'text-red-400' : 'text-zinc-500') },
  ] : [
    { l: 'Arrived (GRN)', v: `${(m?.kg || 0).toLocaleString()} kg`, tone: 'text-emerald-400' },
    { l: 'Arrival value', v: money(m?.amount), tone: 'text-zinc-100' },
    { l: 'Bills (money only)', v: money(m?.billed_only_amount), tone: 'text-zinc-400' },
    { l: 'Expected', v: `${(m?.expected_kg || 0).toLocaleString()} kg`, tone: 'text-blue-400' },
    { l: 'Overdue POs', v: m?.stages?.overdue || 0, tone: (m?.stages?.overdue ? 'text-red-400' : 'text-zinc-500') },
    { l: 'GRNs', v: m?.stages?.arrived || 0, tone: 'text-zinc-100' },
  ];

  return (
    <div className="space-y-3" data-testid={`mv-cal-${direction}`}>
      {/* Flow strip — the interlink */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl px-3 py-2.5 flex items-center gap-3 flex-wrap">
        <span className={`text-[11px] flex items-center gap-1 ${outward ? 'text-zinc-400' : `text-${accent}-300`}`}>
          <ArrowDownLeft size={13} /> Purchase inward
        </span>
        <span className="text-zinc-700">→</span>
        <span className="text-[11px] text-zinc-400">Production</span>
        <span className="text-zinc-700">→</span>
        <span className={`text-[11px] flex items-center gap-1 ${outward ? 'text-emerald-300' : 'text-zinc-400'}`}>
          <ArrowUpRight size={13} /> Sales bill = dispatch out
        </span>
        <span className="flex-1" />
        {cp && (
          <Link to={`${cp.link}?month=${month}`} data-testid="mv-counterpart-link"
            className="text-[11px] text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded-lg px-2.5 py-1">
            {cp.direction === 'inward' ? 'Inward' : 'Outward'} this month: {cp.kg.toLocaleString()} kg · {money(cp.amount)} →
          </Link>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            data-testid="mv-month-prev" className="p-1.5 text-zinc-400 hover:text-zinc-100"><ChevronLeft size={15} /></button>
          <span className="text-[12px] font-heading text-zinc-100 px-1.5 min-w-[120px] text-center"
            data-testid="mv-month-label">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </span>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            data-testid="mv-month-next" className="p-1.5 text-zinc-400 hover:text-zinc-100"><ChevronRight size={15} /></button>
        </div>
        <button onClick={() => { const t = new Date(); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); }}
          data-testid="mv-month-today"
          className="text-[11px] text-amber-300 border border-amber-500/30 rounded-lg px-2 py-1.5 hover:bg-amber-500/10">Today</button>

        <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
          {[['all', 'Both'], ['gst', outward ? 'Tirth Industries (GST)' : 'GST'], ['non_gst', outward ? 'Tirth Enterprises (Non-GST)' : 'Non-GST']].map(([k, l]) => (
            <button key={k} onClick={() => setEntity(k)} data-testid={`mv-entity-${k}`}
              className={`text-[11px] px-2 py-1 rounded-lg ${entity === k ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-100'}`}>
              {l}
            </button>
          ))}
        </div>

        {outward ? (
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
            {[['movement', 'Loaded date'], ['bill', 'Bill date']].map(([k, l]) => (
              <button key={k} onClick={() => setBasis(k)} data-testid={`mv-basis-${k}`}
                className={`text-[11px] px-2 py-1 rounded-lg ${basis === k ? 'bg-zinc-700 text-zinc-50' : 'text-zinc-400 hover:text-zinc-100'}`}>
                {l}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
            {LAYERS.map(({ k, l }) => (
              <button key={k} data-testid={`mv-layer-${k}`}
                onClick={() => setLayers(x => x.includes(k) ? (x.length > 1 ? x.filter(y => y !== k) : x) : [...x, k])}
                className={`text-[11px] px-2 py-1 rounded-lg ${layers.includes(k) ? 'bg-zinc-700 text-zinc-50' : 'text-zinc-500 hover:text-zinc-300'}`}>
                {l}
              </button>
            ))}
          </div>
        )}

        <span className="flex-1" />
        <button onClick={load} data-testid="mv-refresh" className="text-zinc-500 hover:text-zinc-200 p-1.5">
          <RotateCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {cards.map(c => (
          <div key={c.l} className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{c.l}</p>
            <p className={`font-heading text-base ${c.tone}`}>{c.v}</p>
          </div>
        ))}
      </div>

      {/* Entity split */}
      {m && (
        <div className="flex items-center gap-4 flex-wrap text-[11px] text-zinc-400">
          <span className="text-amber-300">
            {outward ? 'Tirth Industries (GST)' : 'GST purchases'}: {m.entities.gst.kg.toLocaleString()} kg · {money(m.entities.gst.amount)} · {m.entities.gst.count} doc(s)
          </span>
          <span className="text-teal-300">
            {outward ? 'Tirth Enterprises (Non-GST)' : 'Non-GST purchases'}: {m.entities.non_gst.kg.toLocaleString()} kg · {money(m.entities.non_gst.amount)} · {m.entities.non_gst.count} doc(s)
          </span>
          <span className="text-zinc-600">{data?.no_double_count}</span>
        </div>
      )}

      {/* Month grid */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-zinc-800">
          {DAYS.map(d => <div key={d} className="text-center text-zinc-600 text-[10px] font-medium py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            if (!date) return <div key={`e${i}`} className="min-h-[92px] border-b border-r border-zinc-800/40 bg-zinc-950/40" />;
            const d = dayMap[date];
            const isToday = date === data?.today;
            const num = Number(date.slice(-2));
            return (
              <button key={date} onClick={() => d && d.events.length && setOpenDate(date)}
                data-testid={`mv-day-${date}`}
                className={`min-h-[92px] text-left border-b border-r border-zinc-800/40 p-1.5 align-top transition-colors
                  ${isToday ? 'bg-amber-500/[0.06]' : 'hover:bg-zinc-800/25'} ${d?.events?.length ? 'cursor-pointer' : 'cursor-default'}`}>
                <div className="flex items-center gap-1">
                  <span className={`text-[11px] font-mono w-5 h-5 flex items-center justify-center rounded-full
                    ${isToday ? 'bg-amber-500 text-black font-bold' : 'text-zinc-500'}`}>{num}</span>
                  {d?.kg > 0 && (
                    <span className={`text-[10px] font-mono ${outward ? 'text-emerald-400' : 'text-amber-300'}`}>
                      {d.kg.toLocaleString()} kg
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {(d?.events || []).slice(0, 3).map(e => (
                    <div key={e.id}
                      className={`text-[9px] px-1 py-0.5 rounded border truncate ${STAGE_TONE[e.stage] || STAGE_TONE.planned}`}>
                      {e.party?.slice(0, 16) || e.doc_no}
                    </div>
                  ))}
                  {(d?.events?.length || 0) > 3 && (
                    <div className="text-[9px] text-zinc-600 pl-1">+{d.events.length - 3} more</div>
                  )}
                </div>
                {d?.money_amount > 0 && (
                  <p className="text-[9px] text-zinc-600 mt-0.5">bills {money(d.money_amount)}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Reconciliation */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <button onClick={() => setShowRecon(x => !x)} data-testid="mv-recon-toggle"
          className="w-full px-3.5 py-2.5 flex items-center gap-2 text-left">
          {recCount > 0 ? <AlertTriangle size={14} className="text-amber-400" /> : <ShieldCheck size={14} className="text-emerald-400" />}
          <span className="text-[12px] text-zinc-100 font-heading">
            {recCount > 0 ? `${recCount} thing(s) don't line up this month` : 'Goods and paper line up this month'}
          </span>
          <span className="flex-1" />
          <span className="text-[10px] text-zinc-500">{showRecon ? 'hide' : 'show'}</span>
        </button>
        {showRecon && (
          <div className="px-3.5 pb-3 space-y-2 text-[11px]" data-testid="mv-recon-body">
            {outward ? (
              <>
                <ReconList title="Billed, still not loaded" tone="text-amber-300" rows={recDir.billed_not_loaded} />
                <ReconList title="Gone without a bill" tone="text-red-300" rows={recDir.gone_not_billed} />
                <ReconList title="Billed and loaded on different days" tone="text-zinc-400" rows={recDir.date_mismatch} />
                {(recDir.double_stock_deduction || []).length > 0 && (
                  <div>
                    <p className="text-red-300 font-semibold">Stock deducted twice (bill + dispatch card)</p>
                    {recDir.double_stock_deduction.map(r => (
                      <p key={r.id} className="text-zinc-300 flex items-center gap-2">
                        {r.date} · {r.party} · {r.doc_no} · {r.qty_kg} kg
                        {isAdmin && (
                          <button onClick={() => fixStock(r.id)} data-testid={`mv-fix-stock-${r.id}`}
                            className="text-[10px] text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5 flex items-center gap-1">
                            <Wrench size={9} /> give the stock back
                          </button>
                        )}
                      </p>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-zinc-400">
                  {recDir.money_only_bills || 0} vendor bill(s) worth {money(recDir.money_only_amount)} have no
                  goods-receipt note — money recorded, arrival not. They are not counted in arrived kg.
                </p>
                <ReconList title="Purchase orders overdue" tone="text-red-300" rows={recDir.overdue_pos} />
                <ReconList title="Arrived without a vendor bill number" tone="text-amber-300"
                  rows={(recDir.grn_without_bill || []).map(r => ({ ...r, doc_no: r.id, why: 'no bill number on the GRN' }))} />
              </>
            )}
          </div>
        )}
      </div>

      {/* The panel reads straight off `dayMap`, so a transport save refreshes
          it in place instead of showing yesterday's answer. */}
      {openDate && dayMap[openDate] && (
        <MovementDayPanel day={dayMap[openDate]} direction={direction} transporters={transporters}
          onClose={() => setOpenDate('')}
          onSaved={load} />
      )}
    </div>
  );
}

function ReconList({ title, tone, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <p className={`${tone} font-semibold`}>{title} — {rows.length}</p>
      {rows.slice(0, 8).map(r => (
        <p key={r.id} className="text-zinc-400">
          {r.date} · {r.party || '—'} · <span className="font-mono">{r.doc_no}</span>
          {r.qty_kg ? ` · ${r.qty_kg} kg` : ''}{r.why ? ` — ${r.why}` : ''}
        </p>
      ))}
      {rows.length > 8 && <p className="text-zinc-600">+{rows.length - 8} more</p>}
    </div>
  );
}
